import { toLooseKey } from '@attain-apps/string-utils'

import type * as common from '../../common'
import type { DocumentNumber } from '../document-number'
import type { ProjectId } from '../project-id'

import { BudRef } from './bud-ref'
import { SourceData } from './source-data'

/**
 * One row of the BCH LOC Population `LOC` tab: a `chglr008` row joined to its
 * award type and, where the prior period's `LOC` tab has a matching `COMBO2`,
 * its `Account P or G` and `Document #`.
 *
 * Only rows whose award type is one of {@link LocRow.TARGET_AWARD_TYPES}
 * become a `LocRow`.
 */
export interface LocRow {
  readonly setId: unknown
  readonly projectId: ProjectId
  /**
   * The `chglr008` `Bud_ref` cell as read, shown unchanged so a numeric `1`
   * stays a number. `COMBO1` and `COMBO2` are built from its parsed
   * {@link BudRef}.
   */
  readonly budRefCell: unknown
  readonly twoDigitBudRef: string
  readonly combo1: string
  readonly combo2: string
  /** `undefined` when the prior period has no matching entry, e.g. a new award. */
  readonly accountPorG?: string
  /** `undefined` when the prior period has no matching entry, e.g. a new award. */
  readonly documentNumber?: DocumentNumber
  readonly awardType: string
  /**
   * The `chglr008` columns that follow `Bud_ref`, verbatim and in their
   * original order.
   */
  readonly carriedColumns: readonly unknown[]
}
export namespace LocRow {
  /** The award types kept on the `LOC` tab; every other type is dropped. */
  export const TARGET_AWARD_TYPES: ReadonlySet<string> = new Set([
    'Deficit-Dept Responsibility',
    'DHHS Letter of Credit',
    'LOC-Accelerated Spending',
  ])

  /**
   * The `LOC` tab's own column headers. Next period's run reads its prior
   * `LOC` tab back by the same names, and the first three are also the
   * `chglr008` headers they come from.
   */
  export const HEADERS = {
    accountPorG: 'Account P or G',
    awardType: 'LOC',
    budRef: 'Bud_ref',
    combo1: 'COMBO1',
    combo2: 'COMBO2',
    documentNumber: 'Document #',
    projectId: 'ProjectID',
    setId: 'SetID',
    twoDigitBudRef: 'Two Digit Bud Ref',
  } as const

  /**
   * How the `LOC` tab shows a missing `Document #` or `Account P or G`,
   * matching the client's workbooks. This period's tab is next period's prior
   * cash draw, so the writer renders it and {@link SourceData.PriorCrosswalk}
   * reads it back as missing.
   */
  export const MISSING = '#N/A'

  /** One of the `LOC` tab's own columns and the value it shows for a row. */
  export interface Column {
    readonly header: string
    /** `undefined` means the value is missing; the writer decides how to show it. */
    readonly value: (row: Readonly<LocRow>) => unknown
  }

  /** The `LOC` tab's own columns, in order. */
  export const COLUMNS = [
    { header: HEADERS.setId, value: row => row.setId ?? null },
    { header: HEADERS.projectId, value: row => row.projectId },
    { header: HEADERS.budRef, value: row => row.budRefCell ?? null },
    { header: HEADERS.twoDigitBudRef, value: row => row.twoDigitBudRef },
    { header: HEADERS.combo1, value: row => row.combo1 },
    { header: HEADERS.combo2, value: row => row.combo2 },
    { header: HEADERS.accountPorG, value: row => row.accountPorG },
    { header: HEADERS.documentNumber, value: row => row.documentNumber },
    { header: HEADERS.awardType, value: row => row.awardType },
  ] as const satisfies readonly Column[]

  /** The `LOC` tab's cell rows. */
  export interface Sheet {
    /** Rows above the column header, such as the report title. */
    readonly preamble: readonly common.SheetRow[]
    readonly header: common.SheetRow
    readonly body: readonly common.SheetRow[]
  }

  /**
   * Builds the `LOC` tab rows from the parsed source files.
   *
   * A `chglr008` row is kept when its project's award type is one of
   * {@link TARGET_AWARD_TYPES}. The type comes from this period's award type
   * query, or, for a project the query does not list, from the prior period's
   * `LOC` tab.
   */
  export function build(data: Readonly<SourceData>): readonly LocRow[] {
    return data.glReport.rows.flatMap(row => {
      const projectKey = toLooseKey(row.projectId)
      const awardType =
        data.awardType.typeByProject.get(projectKey) ??
        data.priorCrosswalk.awardTypeByProject.get(projectKey)
      if (!awardType || !TARGET_AWARD_TYPES.has(awardType)) {
        return []
      }

      const twoDigitBudRef =
        row.budRef === undefined ? '' : BudRef.twoDigit(row.budRef)
      const unpaddedBudRef =
        row.budRef === undefined ? '' : BudRef.unpadded(row.budRef)
      const combo2 = `${row.projectId}${twoDigitBudRef}`
      const prior = data.priorCrosswalk.entryByCombo2.get(toLooseKey(combo2))

      return {
        accountPorG: prior?.accountPorG,
        awardType,
        budRefCell: row.budRefCell,
        carriedColumns: row.carriedColumns,
        combo1: `${row.projectId}${unpaddedBudRef}`,
        combo2,
        documentNumber: prior?.documentNumber,
        projectId: row.projectId,
        setId: row.setId,
        twoDigitBudRef,
      }
    })
  }

  /**
   * Lays out the `LOC` tab: the `chglr008` layout with {@link COLUMNS}
   * replacing everything up to `Bud_ref`, the rows above the header padded to
   * match, then one row per `LocRow`.
   */
  export function toSheet(
    glReport: Readonly<SourceData.GlReport>,
    rows: readonly LocRow[],
  ): Sheet {
    const headerRows = SourceData.GlReport.headerRows(glReport)
    const sourceHeader = headerRows.at(-1) ?? { carried: [], leading: [] }
    // The source columns up to `Bud_ref` are replaced by COLUMNS, so only the
    // difference is new.
    const addedColumnCount = COLUMNS.length - sourceHeader.leading.length

    return {
      body: rows.map(row => [
        ...COLUMNS.map(column => column.value(row)),
        ...row.carriedColumns,
      ]),
      header: [
        ...COLUMNS.map(column => column.header),
        ...sourceHeader.carried,
      ],
      // Every row above the column header (title, section labels) gets blank
      // cells spliced in at the same position, so the layout above the header
      // stays aligned with the columns it describes.
      preamble: headerRows
        .slice(0, -1)
        .map(row => [
          ...row.leading,
          ...new Array<null>(addedColumnCount).fill(null),
          ...row.carried,
        ]),
    }
  }

  /**
   * Whether the prior period had no `Document #` for this row, so a reviewer
   * has to fill it in by hand.
   */
  export function needsDocumentNumber(row: Readonly<LocRow>): boolean {
    return row.documentNumber === undefined
  }

  /**
   * Whether the prior period had no `Account P or G` for this row, so a
   * reviewer has to fill it in by hand.
   */
  export function needsAccountPorG(row: Readonly<LocRow>): boolean {
    return row.accountPorG === undefined
  }
}
